/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { Editor } from '@tiptap/core';
import { gql, useMutation, useQuery } from '@apollo/client';

import {
  Button,
  BackLink,
  Redirect,
  useNavigation,
  useParams,
  useNavPersistedState,
} from '../Navigation';
import {
  ApolloError,
  CircularProgress,
  Divider,
  Heading,
  HStack,
  Input,
  LoadingIndicator,
  ScrollPane,
  Spacer,
  Text,
  VStack,
} from '../../core-ui';
import StatefulEditor, {
  createEditorContent,
} from '../../core-ui/Editor/StatefulEditor';
import { useSeason } from '../SeasonState';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

import MergeTagModal from './MergeTagModal';
import { SenderAddressRequired } from './SenderAddressRequired';
import {
  GetEmail,
  GetEmailVariables,
  GetEmail_email as Email,
} from './__generated__/GetEmail';
import { GetSenderAddresses } from './__generated__/GetSenderAddresses';
import { CreateEmail, CreateEmailVariables } from './__generated__/CreateEmail';
import { UpdateEmail, UpdateEmailVariables } from './__generated__/UpdateEmail';

type TimeoutId = ReturnType<typeof setTimeout>;

const GET_EMAIL = gql`
  query GetEmail($id: String!) {
    email(id: $id) {
      id
      subject
      content {
        value
      }
      job {
        id
      }
      status
    }
  }
`;

const GET_SENDER_ADDRESSES = gql`
  query GetSenderAddresses {
    myAccount {
      id
      senderAddresses {
        id
        uid
        forwardTo {
          address
          isVerified
        }
      }
    }
  }
`;

export const CREATE_EMAIL = gql`
  mutation CreateEmail(
    $seasonId: String!
    $subject: String!
    $content: String!
  ) {
    createEmail(
      seasonId: $seasonId
      data: { subject: $subject, content: $content }
    ) {
      id
    }
  }
`;

const UPDATE_EMAIL = gql`
  mutation UpdateEmail($id: String!, $subject: String!, $content: String!) {
    updateEmail(data: { id: $id, subject: $subject, content: $content }) {
      id
    }
  }
`;

function EmailComposerPage(props: { email: Email | null }) {
  const { email } = props;
  const { navigate } = useNavigation();
  const { season } = useSeason();
  const seasonId = season?.id ?? '';
  const [isMergeTagModalOpen, setMergeTagModalOpen] = useState(false);

  const editorRef = useRef<Editor | null>(null);

  const initialSubject = email?.subject ?? '';
  const [subject, setSubject] = useNavPersistedState(
    'email-subject',
    initialSubject,
  );

  const initialContentString = email?.content.value ?? '';
  const [initialContent] = useState(() =>
    createEditorContent(initialContentString),
  );
  const [content, setContent] = useNavPersistedState(
    'email-content',
    initialContent,
    {
      serialization: {
        serialize: (value) => value.toString(),
        deserialize: (value) => createEditorContent(String(value)),
      },
    },
  );

  const isDirty = content !== initialContent || subject !== initialSubject;
  useEffect(() => {
    if (isDirty) {
      const onBeforeUnload = (event: BeforeUnloadEvent) => {
        if (isDirty) {
          event.preventDefault();
          return (event.returnValue = '');
        }
      };
      window.addEventListener('beforeunload', onBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
      };
    }
  }, [isDirty]);

  const focusTimeoutRef = useRef<TimeoutId | null>(null);
  useEffect(() => {
    return () => {
      const timeout = focusTimeoutRef.current;
      timeout != null && clearTimeout(timeout);
    };
  }, []);

  // This is kinda hacky, but for some reason we can't focus the editor
  // immediately after closing the merge tag dialog.
  const focusEditor = useCallback(() => {
    const timeout = focusTimeoutRef.current;
    timeout != null && clearTimeout(timeout);
    focusTimeoutRef.current = setTimeout(() => {
      editorRef.current?.commands.focus();
    }, 500);
  }, []);

  const nextPageRef = useRef<'drafts' | 'select-recipients'>('drafts');
  const showApolloError = useShowApolloError();

  const [updateEmail, { loading: isSaving1 }] = useMutation<
    UpdateEmail,
    UpdateEmailVariables
  >(UPDATE_EMAIL, {
    onError: showApolloError,
    onCompleted: (data) => {
      if (nextPageRef.current === 'drafts') {
        navigate('/mailer/emails');
      } else {
        const id = data.updateEmail.id;
        navigate('/mailer/emails/:id/recipients', { id });
      }
    },
  });

  const [createEmail, { loading: isSaving2 }] = useMutation<
    CreateEmail,
    CreateEmailVariables
  >(CREATE_EMAIL, {
    onError: showApolloError,
    onCompleted: (data) => {
      if (nextPageRef.current === 'drafts') {
        navigate('/mailer/emails');
      } else {
        const id = data.createEmail.id;
        navigate('/mailer/emails/:id/recipients', { id });
      }
    },
  });

  const isSaving = isSaving1 || isSaving2;

  const saveEmail = async () => {
    if (email) {
      updateEmail({
        variables: {
          id: email.id,
          subject,
          content: content.toString(),
        },
      });
    } else {
      createEmail({
        variables: {
          seasonId,
          subject,
          content: content.toString(),
        },
      });
    }
  };

  const mergeTags = [
    { name: 'name', label: t('Recipient Name') },
    { name: 'email', label: t('Recipient Email Address') },
    { name: 'business_name', label: t('Business/Vendor Name') },
    { name: 'subject', label: t('Email Subject') },
    { name: 'category', label: t('Vendor Category') },
  ];

  return (
    <VStack align="stretch" flex={1} minH={0} px={10} pt={2}>
      <BackLink to={['/mailer/emails']} alignSelf="start" />
      <HStack>
        <Heading as="h2" size="lg">
          {t('Compose Email')}
        </Heading>
        <Spacer />
        {isSaving ? (
          <CircularProgress size="32px" isIndeterminate />
        ) : (
          <>
            <Button
              onClick={() => {
                nextPageRef.current = 'drafts';
                saveEmail();
              }}
            >
              {t('Save as Draft')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                nextPageRef.current = 'select-recipients';
                saveEmail();
              }}
            >
              {t('Send to...')}
            </Button>
          </>
        )}
      </HStack>
      <Divider borderColor="gray.200" />
      <ScrollPane contentContainerProps={{ px: 1, pt: 1, pb: 4 }}>
        <VStack align="stretch">
          <Input
            maxW={400}
            placeholder={t('Subject')}
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value);
            }}
          />
          <StatefulEditor
            height={300}
            maxW={960}
            initialContent={content}
            onChange={(value) => setContent(value)}
            extraButtons={[
              {
                getLabel: () => t('Merge tags'),
                onPress: (editor) => {
                  editorRef.current = editor;
                  setMergeTagModalOpen(true);
                },
              },
            ]}
          />
        </VStack>
      </ScrollPane>
      <MergeTagModal
        isOpen={isMergeTagModalOpen}
        onClose={() => {
          setMergeTagModalOpen(false);
          focusEditor();
        }}
        title={t('Insert Merge Tag')}
        submitButtonText={t('Insert')}
        items={mergeTags}
        renderLabel={(item) => item.label}
        onSubmit={(selectedItem) => {
          const tagName = selectedItem.name;
          editorRef.current?.commands.insertContent(`{${tagName}}`);
          focusEditor();
        }}
      />
    </VStack>
  );
}

/**
 * This component loads an existing email from the server and the displays the
 * composer.
 */
function EmailComposerPageExisting(props: { emailId: string }) {
  const { error, data } = useQuery<GetEmail, GetEmailVariables>(GET_EMAIL, {
    variables: { id: props.emailId },
    fetchPolicy: 'network-only',
  });
  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.email) {
    return <Text>{t('Not Found')}</Text>;
  }
  const email = data.email;
  if (email.status === 'PENDING') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/preview', { id: jobId }]} />;
  }
  if (email.status === 'SENDING') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/progress', { id: jobId }]} />;
  }
  if (email.status === 'SENT') {
    const jobId = email.job?.id ?? '';
    return <Redirect to={['/mailer/jobs/:id/summary', { id: jobId }]} />;
  }
  return <EmailComposerPage email={email} />;
}

export default function EmailComposerPageContainer() {
  const params = useParams();
  const emailId = params.id ?? null;
  const { error, data } = useQuery<GetSenderAddresses>(GET_SENDER_ADDRESSES, {
    fetchPolicy: 'network-only',
  });
  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }

  const senderAddresses = data.myAccount.senderAddresses;
  const verifiedSenderAddresses = senderAddresses.filter(
    (address) => address.forwardTo.isVerified,
  );
  if (verifiedSenderAddresses.length === 0) {
    return <SenderAddressRequired />;
  }

  if (emailId == null) {
    return <EmailComposerPage email={null} />;
  } else {
    return <EmailComposerPageExisting emailId={emailId} />;
  }
}
